/**
 * `/raporty` — the trainer's forms, as KANON `Raporty` (17 September 2026) draws them.
 *
 * ── Composition, and what each piece owns ────────────────────────────────────────────────────
 * The header is the ROSTER's, down to the two squares: `shared/ui/screenHeader.module.css`'s
 * `.titleRow`, `.title`, `.ghost` and `.primary`, at the 36px cut `/` and `/plans` draw. The canon
 * gave this screen a 44px `+` and an eyebrow over the title; the owner took both back on 17
 * September 2026 — three header bands behaving differently is three answers to one question, and the
 * eyebrow said nothing the title and the tab strip under it do not.
 *
 * It is not the `ScreenHeader` COMPONENT, and that is the one deliberate difference: that component
 * always draws a `<dl>` of statistics under the title, this screen has none to draw, and an empty
 * definition list plus its row gap is geometry nobody asked for. The classes are that component's
 * own published surface — see its header — so the squares still cannot drift.
 *
 * `shared/ui/TabStrip` is the strip, promoted out of the client detail on this screen's account.
 * `SurveyColumn` draws the list and its four states; `SurveyDetailCard` describes whichever form is
 * pointed at and owns its own read.
 *
 * ── Only the visible tab is mounted ──────────────────────────────────────────────────────────
 * Each tab owns its reads, so switching to „Kto ma którą" is the only thing that fetches the matrix.
 * That is also why the strip activates MANUALLY: arrowing across three tabs that each fetch would
 * fire three tabs' worth of requests on the way past and push three history entries with them.
 *
 * ── Nothing on this screen moves when data lands ─────────────────────────────────────────────
 * The header renders in every state, the strip is a fixed band, the column and the card are laid out
 * by the stylesheet, and both waits are skeletons inside boxes that already have their size. The
 * gate hides the bars, not the box. Verify it with latency injected — the local backend answers in
 * about 5ms and hides everything above.
 *
 * ── The screen ENTERS, once, as one object ───────────────────────────────────────────────────
 * The same step `/plans` takes, for the same reason: a master-detail pane is one object.
 * `prefers-reduced-motion` is a PARAMETER — `base.css`'s duration cap governs CSS animations and
 * Motion drives the Web Animations API, so a surface that relied on that block would ignore the
 * preference while looking compliant.
 */

import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';

import { useState } from 'react';

import { screenMotion, useScreenMotionPreference } from '~/shared/design-system/motion';
import { TabStrip, tabButtonId, tabPanelId } from '~/shared/ui/TabStrip';
import ember from '~/shared/ui/emberButton.module.css';
import header from '~/shared/ui/screenHeader.module.css';
import shell from '~/shared/ui/canonScreen.module.css';
import { ScreenHeaderPlus } from '~/shared/ui/ScreenHeader';
import menuStyles from '~/shared/ui/MenuSurface.module.css';
import { Menu } from '@base-ui/react/menu';
import { Link } from '@tanstack/react-router';

import { NO_SURVEY_FACETS, type SurveyFacets } from '../model/surveyFilters';
import { REPORT_TABS, reportTabLabel, type ReportTab } from '../model/reportTabs';

import { useSurveysTab } from '../lib/useSurveysTab';
import { MatrixTab } from './MatrixTab';
import { MoreIcon } from '~/shared/ui/ReportIcons';
import { ReviewQueueTab } from './ReviewQueueTab';
import { SurveyColumn } from './SurveyColumn';
import { SurveyDetailCard } from './SurveyDetailCard';
import { SurveyTools } from './SurveyTools';
import styles from './ReportsScreen.module.css';

interface ReportsScreenProps {
  readonly tab: ReportTab;
  readonly onSelectTab: (tab: ReportTab) => void;
}

interface SurveysTabProps {
  readonly facets: SurveyFacets;
  readonly onChange: (facets: SurveyFacets) => void;
}

function SurveysTab({ facets, onChange }: SurveysTabProps) {
  const view = useSurveysTab(facets);

  return (
    <div className={styles.columns}>
      <SurveyColumn
        state={view.listState}
        skeleton={view.listSkeleton}
        reveal={view.listReveal}
        groups={view.groups}
        facets={facets}
        faces={view.faces}
        selectedId={view.selected?.id ?? null}
        onSelect={view.onSelect}
        pausingId={view.pausingId}
        onSetActive={view.onSetActive}
        onClearFilters={() => {
          // The phrase goes with the facets here, unlike the menu's own „Wyczyść filtry": the
          // trainer is standing in front of an empty column and the button says so — clearing one
          // of the two narrowings and leaving the other would leave them looking at the same empty
          // column under a button they had just pressed.
          onChange(NO_SURVEY_FACETS);
        }}
        onRetry={view.onRetry}
      />
      <SurveyDetailCard
        state={view.detailState}
        skeleton={view.detailSkeleton}
        reveal={view.detailReveal}
        row={view.selected}
        sections={view.sections}
      />
    </div>
  );
}

export function ReportsScreen({ tab, onSelectTab }: ReportsScreenProps) {
  const { t } = useTranslation();
  const reducedMotion = useScreenMotionPreference();

  /**
   * What the Ankiety column is narrowed by.
   *
   * It lives here because the controls that set it stand in the tab strip's row, which is the
   * screen's chrome rather than the tab's panel — see `SurveyTools`. It SURVIVES a visit to another
   * tab, which is the same promise the roster's own filters make: a narrowing the trainer set is
   * theirs until they clear it.
   */
  const [facets, setFacets] = useState<SurveyFacets>(NO_SURVEY_FACETS);

  return (
    <main className={shell.page}>
      <motion.div className={shell.measure} {...screenMotion('rise', { reducedMotion })}>
        <div className={styles.header}>
          <div className={header.titleRow}>
            {/* `AppShell` finds the screen's `h1` and moves focus to it after a route change, so it
              * is rendered in every state rather than waiting for a read. */}
            <h1 className={`${String(header.title)} ${String(styles.title)}`}>
              {t('web:reports.title')}
            </h1>

            <Menu.Root>
              <Menu.Trigger
                className={header.ghost}
                aria-label={t('web:reports.menu.trigger')}
                title={t('web:reports.menu.trigger')}
              >
                <MoreIcon className={header.ghostIcon} />
              </Menu.Trigger>
              <Menu.Portal>
                <Menu.Positioner className={menuStyles.positioner} sideOffset={6} align="end">
                  <Menu.Popup className={menuStyles.popup}>
                    {/* The menu's only entry in v1. „Usunięte ankiety" was cut on 17 September 2026
                      * — the backend serves no read of deleted templates, so the entry would have
                      * been a promise nothing could keep. */}
                    <Menu.Item className={menuStyles.item} render={<Link to="/raporty/katalog" />}>
                      <span className={menuStyles.itemLabel}>{t('web:reports.menu.catalog')}</span>
                    </Menu.Item>
                  </Menu.Popup>
                </Menu.Positioner>
              </Menu.Portal>
            </Menu.Root>

            {/* Icon-only, as the canon draws it: `+` over „Nowa ankieta". The name is on the
              * link, which is the only place a listener can read it. */}
            <Link
              to="/raporty/nowa"
              className={`${String(ember.ember)} ${String(header.primary)}`}
              aria-label={t('web:reports.newSurvey')}
              title={t('web:reports.newSurvey')}
            >
              <ScreenHeaderPlus className={header.primaryIcon} />
            </Link>
          </div>
        </div>

        <TabStrip
          className={styles.tabBar}
          label={t('web:reports.tabsLabel')}
          tabs={REPORT_TABS}
          selected={tab}
          renderLabel={(entry) => reportTabLabel(entry, t)}
          onSelect={onSelectTab}
          trailing={tab === 'ankiety' ? <SurveyTools facets={facets} onChange={setFacets} /> : null}
        />

        {/* `tabIndex={0}` on the panel, on every tab rather than on the ones that need it: the tabs
          * pattern requires it wherever a panel holds nothing focusable, and the alternative is a
          * rule that watches its own children.
          *
          * The `key` is what replays the entrance — a new key is a new element, and the element is
          * the tab's content, which genuinely changed. */}
        <motion.div
          key={tab}
          className={styles.panel}
          role="tabpanel"
          id={tabPanelId(tab)}
          aria-labelledby={tabButtonId(tab)}
          tabIndex={0}
          {...screenMotion('enter', { reducedMotion })}
        >
          {/* A TABLE keyed by the tab, not a chain of ternaries: `REPORT_TABS` is the declared
            * source of truth for which tabs exist, and a ternary chain's last `else` would silently
            * mean one of them. A `Record<ReportTab, …>` makes a fourth tab a compile error. */}
          {(
            {
              ankiety: () => <SurveysTab facets={facets} onChange={setFacets} />,
              kto: () => <MatrixTab />,
              przegladu: () => <ReviewQueueTab />,
            } satisfies Record<ReportTab, () => React.JSX.Element>
          )[tab]()}
        </motion.div>
      </motion.div>
    </main>
  );
}
